//import 'ol/ol.css';
import { Vector as VectorLayer, Tile as TileLayer, Image as ImageLayer } from 'ol/layer';
import { Vector as VectorSource, OSM as OSMSource, ImageWMS, TileWMS, XYZ, TileArcGISRest } from 'ol/source';
import { GeoJSON, WFS } from 'ol/format';
import BingMaps from 'ol/source/BingMaps';
import { all, bbox } from 'ol/loadingstrategy';
import * as AuthModel from '../Models/AuthModel';
import * as events from '../Bus/Bus';
import { CoreLayerConfig, AdditionalLayerConfig, BaseLayerConfig, MapInitialState, BaseLayerType, MapConfigModel, AdditionalMapLayerType, MapLayerGroupType } from '../Models/MapConfigModels';
import { MapStyles } from './MapStyles';
import TileGrid from 'ol/tilegrid/TileGrid';
import { CAMSImageLayer, CAMSTileLayer, CAMSVectorSource, CAMSVectorLayer, CAMSMap } from '../../Extensions/CAMSOLExtensions';
import * as olExtent from 'ol/extent'
import { LocalStorageSettingsModel } from '../Models/LocalStorageSettingsModel';
import { FeatureType } from '../Models/UtilityModels';
import { Feature } from 'ol';
import { LineString } from 'ol/geom';
import { Style, Stroke } from 'ol/style';
import { Utilities } from '../Utility/Utilities';

export interface kvp {
    [key: string]: any;
}

export class MapLayersHelper {

    // ----------------------------------------------------------- //
    // GLOBAL VARS
    private percentToBufferExtent = 0.3;
    private delayBeforeLoadingWFS = 300;
    private loadingLayers: kvp = []; 
    private previousExtent: kvp = [];
    private localStorageSettings: LocalStorageSettingsModel;

    // ----------------------------------------------------------- //
    // MAP FILTER functions
    private mapFilterAnnotationUID: number = 1;
    public getMapFilterAnnotationUID: Function = function () { return "&AnnotationUID=" + this.mapFilterAnnotationUID; }.bind(this);
    public setMapFilterAnnotationUID: Function = function (annotationUID: number) { const self = this; self.mapFilterAnnotationUID = annotationUID; }

    private mapFilterMaint: string;
    public getMapFilterMaint: Function = function () { return this.mapFilterMaint; }.bind(this);

    private mapFilterMyMaint: string;
    public getMapFilterMyMaint: Function = function () { return this.mapFilterMyMaint; }.bind(this);

    // ----------------------------------------------------------- //
    // Constructor
    public constructor(
        protected authModel: AuthModel.IAuthModel,
        protected mapConfigModel: MapConfigModel,
        protected mapStyles: MapStyles)
    {
        const self = this;

        let localStorageSettings_str = window.localStorage.getItem('localStorageSettings');
        self.localStorageSettings = JSON.parse(localStorageSettings_str);
        if (self.localStorageSettings.Layers == null) self.localStorageSettings.Layers = [];
    }

    // ----------------------------------------------------------- //
    // Public functions: BASE LAYERS
    public createBaseLayersFromConfig = (baselayerConfig: Array<BaseLayerConfig>): any[] => {
        let layers = [];
        baselayerConfig.filter(e => e.authPolicies.length == 0 || e.authPolicies.some(r => this.authModel.policies.indexOf(r) >= 0) == true).forEach(e => {
            if (e.enabled == true) {
                switch (e.baseLayerType) {
                    case BaseLayerType.TiledWMS: {
                        let params = JSON.parse(e.params);
                        let source = new TileWMS({
                            urls: e.baseURLs,
                            params: params,
                            serverType: 'geoserver',
                            attributions: [e.attribution],
                            crossOrigin: 'Anonymous',
                            tileGrid: new TileGrid({
                                origin: [0,0],
                                resolutions: [2000, 1000, 500, 250, 100, 50, 25, 10, 5, 2.5, 1, 0.5, 0.25]
                            }),
                        });

                        let layer = new CAMSTileLayer({
                            source: source,
                            //minZoom: e.minZoom,
                            opacity: e.opacity,
                            visible: e.visibleOnStartup
                        });
                        layer.DisplayName = e.displayName;
                        layers.push(layer);
                        break;
                    }

                    case BaseLayerType.TileXYZ: {
                        let source = new XYZ({
                            url: e.baseURLs[0],
                            projection: this.mapConfigModel.mapInitialState.mapBaseProjection,
                            attributions: [e.attribution],
                            crossOrigin: 'Anonymous',
                            tileGrid: new TileGrid({
                                origin: [-238375.0, 1376256.0],
                                resolutions: [896.0, 448.0, 224.0, 112.0, 56.0, 28.0, 14.0, 7.0, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375]
                            })
                        });

                        let layer = new CAMSTileLayer({
                            source: source,
                            //minZoom: e.minZoom,
                            opacity: e.opacity,
                            visible: e.visibleOnStartup
                        });
                        layer.DisplayName = e.displayName;
                        layers.push(layer);
                        break;
                    }

                    case BaseLayerType.OSM: {
                        let layer = new CAMSTileLayer({
                            source: new OSMSource(),
                            opacity: e.opacity,
                            //minZoom: e.minZoom,
                            visible: e.visibleOnStartup
                        });
                        layer.DisplayName = e.displayName;
                        layers.push(layer);
                        break;
                    }

                    case BaseLayerType.Bing: {
                        // https://openlayers.org/en/latest/examples/bing-maps.html
                        let params = JSON.parse(e.params);
                        let layer = new CAMSTileLayer({
                            visible: false,
                            opacity: e.opacity,
                            preload: Infinity,
                            source: new BingMaps({
                                key: params.apikey,
                                imagerySet: 'Aerial',
                                // use maxZoom 19 to see stretched tiles instead of the BingMaps
                                // "no photos at this zoom level" tiles
                                // maxZoom: 19
                            })
                        });
                        layer.DisplayName = e.displayName; 
                        layers.push(layer);
                        break;
                    }
                }
            }
        });

        return layers;
    }

    // ----------------------------------------------------------- //
    // Public functions: ADDITIONAL LAYERS
    public createAdditionalLayersFromConfig = (additionalLayers: Array<AdditionalLayerConfig>): any[] => {
        let layers = [];
        additionalLayers.forEach(e => {
            try {
                if (e.enabled == true) {
                    switch (e.additionalMapLayerType) {
                        case AdditionalMapLayerType.ImageWMS: {
                            let params = JSON.parse(e.params);
                            var wmsSource = new ImageWMS({
                                url: e.baseURL,
                                params: params,
                                serverType: 'mapserver',
                                crossOrigin: 'anonymous'
                            });

                            var wmsLayer = new CAMSImageLayer({
                                source: wmsSource,
                                //minZoom: e.minZoom,
                                visible: e.visibleOnStartup
                            });
                            wmsLayer.DisplayName = e.legendStyle.legendName;
                            wmsLayer.CanInfoClick = e.canInfoClick;

                            layers.push(wmsLayer);
                            break;
                        }

                        case AdditionalMapLayerType.TileArcGISRest: {
                            let params = JSON.parse(e.params);
                            var arcGISSource = new TileArcGISRest({
                                url: e.baseURL,
                                params: params,
                                crossOrigin: 'anonymous'
                            });

                            var arcGISLayer = new CAMSTileLayer({
                                source: arcGISSource,
                                //minZoom: e.minZoom,
                                visible: e.visibleOnStartup
                            });
                            arcGISLayer.DisplayName = e.legendStyle.legendName;
                            arcGISLayer.CanInfoClick = e.canInfoClick;

                            layers.push(arcGISLayer);
                            break;
                        }

                        case AdditionalMapLayerType.GeoJSON: {
                            var visible = e.visibleOnStartup;
                            if (e.enabled == false) visible = false;

                            const vectorLayer = new CAMSVectorLayer({
                                style: new Style({
                                    stroke: new Stroke({
                                        color: "rgba(255,0,0,2)",
                                        width: 1
                                    })
                                }),
                                //minZoom: defaults.minZoom,
                                visible: visible,
                                declutter: false
                            });

                            const vectorSource = this.createLayerSource(null, "EPSG:27700", null, vectorLayer, e.baseURL, e.params);
                            vectorLayer.setSource(vectorSource);
                            vectorLayer.set("layerName", e.legendStyle.legendName); // Set the layer name
                            vectorLayer.DisplayName = e.legendStyle.legendName;
                            vectorLayer.CanInfoClick = e.canInfoClick;

                            layers.push(vectorLayer);
                            break;
                        }
                    }
                }
            }
            catch (e) {
                console.log("MapLayersHelper.createAdditionalLayersFromConfig: Error creating additional issue: "+e);
            }
        });
     
        return layers;
    }

    // ----------------------------------------------------------- //
    // Public functions: CORE LAYERS
    public setupLandLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Land).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.LandMapStyles.landStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupTROLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.TRO).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, MapStyles.troStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupRouteLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Route).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.RouteMapStyles.routeStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupInfraLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Infra).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.InfraMapStyles.infraStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupInfraHistoricLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.InfraHistoric).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.InfraMapStyles.infraStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupLegalRouteLayers = (): void => {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalRoute).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.LegalRouteMapStyles.legalRouteStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupLegalFeatureLayers = (): void => {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalFeature).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.LegalFeatureMapStyles.legalFeatureStyleFunction, isAuthorized);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupMaintLayers = (): void =>
    {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Maint).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.MaintMapStyles.maintStyleFunction, isAuthorized, self.getMapFilterMaint);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupMyMaintLayers = (): void =>
    {
        const self = this;
        self.mapFilterMyMaint = '&LogBy=' + self.mapConfigModel.contactRef;

        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.MyMaint).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, self.mapStyles.MaintMapStyles.maintStyleFunction, isAuthorized, self.getMapFilterMyMaint);
                l.olLayers.push(layer);
            }
        });
    } 

    public setupAnnotationLayers = (): void => {
        const self = this;
        self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Annotation).forEach(l => {
            var layerKey = l.layerGroupName + "_" + l.legendName;
            var c = self.localStorageSettings.Layers.find(c => c.LayerKey == layerKey);
            if (self.localStorageSettings.Layers != null) { if (c != null) l.visibleOnStartup = c.Visible; }

            l.olLayers = [];
            if (l.wfsPointName != null) { l.wfsName = l.wfsPointName; addLayer(); }
            if (l.wfsLineName != null) { l.wfsName = l.wfsLineName; addLayer(); }
            if (l.wfsPolyName != null) { l.wfsName = l.wfsPolyName; addLayer(); }

            function addLayer() {
                var isAuthorized = self.checkAuthPolicies(l, self.mapConfigModel.contactRef);
                var layer = self.createLayerFromWFS(l, self.mapConfigModel.mapInitialState, MapStyles.annotationStyleFunction, isAuthorized, self.getMapFilterAnnotationUID);
                l.olLayers.push(layer);
            }
        });
    } 

    // ----------------------------------------------------------- //
    //Layer Functions
    private createLayerFromWFS = (defaults: CoreLayerConfig, mapInitialState: MapInitialState, style: Function, isAuthorized: boolean, filterFunction: Function = null): VectorLayer => {
        var self = this;

        var visible = defaults.visibleOnStartup;
        if (defaults.enabled == false) visible = false;

        const vectorLayer = new VectorLayer({
            style: function (feature, resolution) {
                return style(feature, resolution, vectorLayer, self);
            },
            //minZoom: defaults.minZoom,
            visible: visible,
            declutter: false
        });

        // Only set the source if the user is authorized
        if (isAuthorized == true) {
            const vectorSource = this.createLayerSource(defaults.wfsName, mapInitialState.mapBaseProjection, filterFunction, vectorLayer);
            vectorLayer.setSource(vectorSource);
        }
        else {
            vectorLayer.setVisible(false);
        }
        vectorLayer.set("layerName", defaults.wfsName); // Set the layer name
        vectorLayer.set("isAuthorized", isAuthorized); // Set isAuthorized
        vectorLayer.set("symbologyKey", defaults.symbologyKey); // Set isAuthorized

        return vectorLayer;
    }

    private createLayerSource = (wfsName: string, mapBaseProjection: string, filterFunction: Function = null, vectorLayer: VectorLayer, baseURLOverride: string = null, paramsStr: string = null): VectorSource => {
        const self = this;

        const vectorSource = new CAMSVectorSource({
            format: new GeoJSON(),
            loader: loadData,
            strategy: bbox
        });
        
        function loadData(extent, resolution, projection) {
            // Expand extent and remove decimal points
            let widthInMetres = olExtent.getWidth(extent);
            let buffered = olExtent.buffer(extent, widthInMetres * self.percentToBufferExtent);
            buffered[0] = parseFloat(buffered[0].toFixed(1));
            buffered[1] = parseFloat(buffered[1].toFixed(1));
            buffered[2] = parseFloat(buffered[2].toFixed(1));
            buffered[3] = parseFloat(buffered[3].toFixed(1));

            // Only allow one request to proceed, if the user keeps panning
            // discard all the other requests and just run the last one
            var loader = this;
            if (loader != undefined) {
                if (self.loadingLayers[loader.ol_uid] == undefined) {
                    self.loadingLayers[loader.ol_uid] = { retry: false, extent: null };
                }
                else {
                    self.loadingLayers[loader.ol_uid] = { retry: true, extent: extent };
                    return;
                }

                // Check if the expanded extent 
                if (self.previousExtent[loader.ol_uid] == undefined) {
                    self.previousExtent[loader.ol_uid] = buffered;
                }
                else {
                    if (loader.forceRefreshFlag == true) {
                        self.previousExtent[loader.ol_uid] = buffered;
                    }
                    else if (olExtent.containsExtent(self.previousExtent[loader.ol_uid], extent) == true) {
                        if (loader != undefined && self.loadingLayers[loader.ol_uid] != undefined) {
                            self.loadingLayers[loader.ol_uid] = undefined;
                        }
                        return;
                    }
                    else {
                        self.previousExtent[loader.ol_uid] = buffered;
                    }
                }

                // Reset the forceRefreshFlag
                loader.foreceRefreshFlag = false;
            }

            // Create URL
            let filterStr = filterFunction == null ? "" : filterFunction();
            let url = `/api/MapServer/CamsPro?service=WFS&` +
                `version=1.1.0&request=GetFeature&typename=${wfsName}&` +
                `outputFormat=geojson&srsname=${mapBaseProjection}&` +
                `bbox=${buffered.join(',')},${mapBaseProjection}` + filterStr;

            if (baseURLOverride != null) {
                url = `${baseURLOverride}`;
                let params = JSON.parse(paramsStr);
                Object.keys(params).forEach(k => {
                    url += `${k}=${params[k]}&`;
                });
                url += `bbox=${buffered.join(',')},${mapBaseProjection}`;
            }

            let xhr = new XMLHttpRequest();
            xhr.open('GET', url);
            let onError = function () {
                let xx: any = vectorSource;
                //vectorSource.removeLoadedExtent(extent);
                xx.removeLoadedExtent(buffered);
            };

            xhr.onerror = onError;
            xhr.onload = function () {

                // Only run the very first and very last requests per layer
                if (loader != undefined && self.loadingLayers[loader.ol_uid] != undefined && self.loadingLayers[loader.ol_uid].retry == true) {
                    loadData(self.loadingLayers[loader.ol_uid].extent, null, null);
                    self.loadingLayers[loader.ol_uid] = undefined;
                }
                else {
                    if (loader != undefined) self.loadingLayers[loader.ol_uid] = undefined;
                    if (xhr.status == 200) {
                        try {
                            let features: any = vectorSource.getFormat();

                            // Remove previous features
                            vectorSource.clear(true);
                            
                            // Add features
                            vectorSource.addFeatures(features.readFeatures(xhr.responseText));

                            // Check features for multiple rings
                            //vectorSource.getFeatures().forEach(f => {
                            //    var g = f.getGeometry();
                            //    if (g.getType() == GeometryType.POLYGON) {
                            //        var p = g as Polygon;
                            //        if (p.getLinearRingCount() > 1) {
                            //            var m = new MultiPolygon([]);
                            //            p.getLinearRings().forEach(r => {
                            //                var c1 = [r.getCoordinates()];
                            //                let p1 = new Polygon(c1);
                            //                m.appendPolygon(p1);
                            //            });
                            //            f.setGeometry(m);
                            //        }
                            //    }

                            //});

                            // Fire completed event
                            $(document).trigger('mapLayerUpdated', vectorLayer);
                        }
                        catch (e) {
                            console.error("Error MapLayerHelper: "+JSON.stringify(e));
                        }
                    } else {
                        onError();
                    }
                }
            };

            // delay the request to allow WMS tiles to appear first
            setTimeout(function () { xhr.send(); }, self.delayBeforeLoadingWFS);
        }

        return vectorSource;
    }

    public checkAuthPolicies = (layerConfig: CoreLayerConfig, contactRef: string): boolean => 
    {
        var self = this;

        // Check if only use anon setting is true
        if (contactRef == null && layerConfig.onlyShowIfUserIsAnonymous == true) return true;
        else if (contactRef != null && layerConfig.onlyShowIfUserIsAnonymous == true) return false;

        // Check if the config has any auth polciies, if not, show the layer
        if (layerConfig.authPolicies.length == 0) return true;

        // If it DOES have policies and the authmodel also has them, return true
        // If the auth model does not have them, return false
        if (layerConfig.authPolicies.some(r => this.authModel.policies.indexOf(r) >= 0) == true) {
            return true;
        }
        else {
            return false; 
        }
    }

    // ****************************************************************** //
    // GRID Layer
    // ****************************************************************** //
    public createGridLayer = (): VectorLayer => {
        const self = this;
        
        const features = [];
        for (let i = 100000; i <= 700000; i += 1000) {
            features.push(
                new Feature(
                    new LineString([[i, 100000], [i, 700000]])
                )
            );
        };
        for (let i = 100000; i <= 700000; i += 1000) {
            features.push(
                new Feature(
                    new LineString([[100000, i], [700000, i]])
                )
            );
        };

        var layer =
            new VectorLayer({
                source: new VectorSource({
                    features: features
                }),
                style: new Style({
                    stroke: new Stroke({
                        color: "rgba(0,0,0,0.3)",
                        width: 1
                    }),
                }),
                minZoom: 12,
                visible: this.mapConfigModel.gridLayer.visibleOnStartup
            });

        return layer;
    }
}




